import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';

import betterflyLogo from '../assets/betterfly-logo-solutions.png';
import betterflyLogoWhite from '../assets/betterfly-logo-solutions-white.png';
import betterflyMarkWhite from '../assets/betterfly-mark-white.png';
import equalityLogo from '../assets/equality-logo.png';
import { loginEvent } from '../Context/events';
import { loginUser } from '../servicesV2/authService';
import { Button, Field, Input } from '../components/ui';
import { useTheme } from '../components/shell/useTheme';

// Grid cells lit on the brand panel — each one stands for a client space
// managed from this console. Coordinates are in 48px grid units; colours are
// the logo's blue, and its orange for the single highlighted cell.
const LOGO_BLUE = '52, 171, 252';
const LOGO_ORANGE = '240, 166, 57';
// Kept within the first 8 columns so nothing is clipped at the 400px minimum panel width.
const LIT_CELLS = [
  { col: 5, row: 2, tone: 0.2 },
  { col: 6, row: 2, tone: 0.38 },
  { col: 6, row: 3, tone: 0.2 },
  { col: 3, row: 4, tone: 0.38 },
  { col: 7, row: 4, tone: 1, color: LOGO_ORANGE },
  { col: 1, row: 6, tone: 0.2 },
  { col: 5, row: 6, tone: 0.38 },
];

// Backend errors come back as { code, message }; rate limiting answers 429 with { error }.
function errorCodeFrom(err) {
  if (!err.response) return 'ERR_NETWORK';
  if (err.response.status === 429) return 'TOO_MANY_ATTEMPTS';
  return err.response.data?.code || 'ERROR';
}

export default function Login() {
  const { t } = useTranslation();
  useTheme(); // applies the stored light/dark preference before the shell mounts

  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [isPassHidden, setIsPassHidden] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState('');

  const submitLogin = async (event) => {
    event.preventDefault();
    if (email.trim().length === 0 || pass.length === 0) {
      setErrorCode('ALL_REQUIRED');
      return;
    }
    setErrorCode('');
    setLoading(true);
    try {
      await loginUser({ Email: email.trim(), password: pass });
      loginEvent.emit('login', true);
    } catch (err) {
      setErrorCode(errorCodeFrom(err));
      setLoading(false);
    }
  };

  const errorMessage = errorCode
    ? t(`ERRORS.${errorCode}`, { defaultValue: t('ERRORS.ERROR') })
    : '';

  return (
    <div className="min-h-screen bg-surface text-fg lg:grid lg:grid-cols-[clamp(400px,38vw,640px)_minmax(0,1fr)]">
      <aside className="login-panel relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-end p-12 text-white">
        {LIT_CELLS.map(({ col, row, tone, color = LOGO_BLUE }, i) => (
          <span
            key={`${col}-${row}`}
            aria-hidden
            className="login-cell absolute"
            style={{
              left: col * 48 + 1,
              top: row * 48 + 1,
              backgroundColor: `rgba(${color}, ${tone})`,
              animationDelay: `${120 + i * 70}ms`,
            }}
          />
        ))}

        <div className="relative">
          <img src={betterflyMarkWhite} alt="" aria-hidden className="h-16 w-auto" />
          <p className="mt-6 text-[44px] font-semibold leading-none tracking-[-0.02em]">
            Betterfly Console
          </p>
        </div>
      </aside>

      <main className="flex min-h-screen flex-col px-4 py-10 sm:px-8">
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-[380px]">
            <div className="mb-10">
              <img src={betterflyLogo} alt="Betterfly Solutions" className="h-[72px] w-auto dark:hidden" />
              <img
                src={betterflyLogoWhite}
                alt="Betterfly Solutions"
                className="hidden h-[72px] w-auto dark:block"
              />
            </div>

            <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.015em]">
              Connexion
            </h1>

            <form onSubmit={submitLogin} noValidate className="mt-8 flex flex-col gap-5">
              {errorMessage && (
                <div
                  role="alert"
                  className="flex items-start gap-2.5 rounded-md border border-danger-500/40 bg-danger-50 px-3.5 py-3 text-[14px] text-danger-700 dark:bg-danger-500/10 dark:text-danger-500"
                >
                  <FiAlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden />
                  <span>{errorMessage}</span>
                </div>
              )}

              <Field label="Adresse e-mail" id="login-email" className="[&_label]:text-[14px]">
                <Input
                  id="login-email"
                  type="email"
                  size="lg"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  invalid={errorCode === 'ALL_REQUIRED' && email.trim().length === 0}
                  className="h-11 text-[15px]"
                />
              </Field>

              <Field label="Mot de passe" id="login-password" className="[&_label]:text-[14px]">
                <div className="relative">
                  <Input
                    id="login-password"
                    type={isPassHidden ? 'password' : 'text'}
                    size="lg"
                    autoComplete="current-password"
                    value={pass}
                    onChange={(e) => setPass(e.target.value)}
                    invalid={errorCode === 'ALL_REQUIRED' && pass.length === 0}
                    className="h-11 pr-11 text-[15px]"
                  />
                  <button
                    type="button"
                    onClick={() => setIsPassHidden((hidden) => !hidden)}
                    aria-label={isPassHidden ? 'Afficher le mot de passe' : 'Masquer le mot de passe'}
                    aria-pressed={!isPassHidden}
                    className="absolute inset-y-0 right-0 inline-flex w-11 items-center justify-center rounded-r-md text-fg-subtle hover:text-fg"
                  >
                    {isPassHidden ? <FiEye size={17} /> : <FiEyeOff size={17} />}
                  </button>
                </div>
              </Field>

              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={loading}
                className="mt-2 h-11 text-[15px] font-semibold"
              >
                {loading ? 'Connexion en cours' : 'Se connecter'}
              </Button>
            </form>
          </div>
        </div>

        <footer className="mx-auto flex w-full max-w-[380px] items-center gap-3 pt-10 text-[13px] text-fg-muted">
          <span>Édité par</span>
          <img
            src={equalityLogo}
            alt="E-Quality Engineering"
            className="h-10 w-auto dark:invert dark:hue-rotate-180 dark:mix-blend-screen"
          />
        </footer>
      </main>
    </div>
  );
}
